/**
 * Product specification tabs
 * ---------------------------------------------------------------
 * Self-initialising, framework-free tab component.
 * Markup contract (see snippets/specification-tabs.liquid):
 *
 *   <div class="spec-tabs" data-spec-tabs>
 *     <div class="spec-tabs__tablist" role="tablist">
 *       <button class="spec-tabs__tab is-active"
 *               role="tab"
 *               data-spec-target="paneId">…</button>
 *       …
 *     </div>
 *     <div class="spec-tabs__panels">
 *       <section class="spec-tabs__panel is-active" id="paneId" role="tabpanel">…</section>
 *       …
 *     </div>
 *   </div>
 *
 * Behaviour:
 *  - Click / Enter / Space on a tab activates the matching panel.
 *  - Arrow Left/Right + Home/End cycle focus + activation (WAI-ARIA).
 *  - Uses event delegation on `document`, so it works for tabs that
 *    are added to the DOM after page load (variant swaps, AJAX, etc.).
 */

(function () {
    const ROOT_SELECTOR  = '[data-spec-tabs]';
    const TAB_SELECTOR   = '.spec-tabs__tab';
    const PANEL_SELECTOR = '.spec-tabs__panel';

    /**
     * Activate a tab + its panel inside the given root.
     * @param {HTMLElement} root
     * @param {HTMLElement} tab
     */
    function activate(root, tab) {
        const targetId = tab.getAttribute('data-spec-target');
        if (!targetId) return;

        const escapedId = (window.CSS && CSS.escape) ? CSS.escape(targetId) : targetId;
        const panel = root.querySelector('#' + escapedId);
        if (!panel) return;

        root.querySelectorAll(TAB_SELECTOR).forEach(t => {
            const on = t === tab;
            t.classList.toggle('is-active', on);
            t.setAttribute('aria-selected', on ? 'true' : 'false');
            t.setAttribute('tabindex', on ? '0' : '-1');
        });

        root.querySelectorAll(PANEL_SELECTOR).forEach(p => {
            const on = p === panel;
            p.classList.toggle('is-active', on);
            if (on) p.removeAttribute('hidden');
            else    p.setAttribute('hidden', '');
        });
    }

    /**
     * Click handler (delegated). Survives DOM swaps and works
     * regardless of script vs. snippet load order.
     */
    document.addEventListener('click', event => {
        const tab = event.target.closest(TAB_SELECTOR);
        if (!tab) return;

        const root = tab.closest(ROOT_SELECTOR);
        if (!root) return;

        event.preventDefault();
        activate(root, tab);
    });

    /**
     * Keyboard navigation inside a tablist (delegated).
     */
    document.addEventListener('keydown', event => {
        const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (keys.indexOf(event.key) === -1) return;

        const tab = event.target.closest(TAB_SELECTOR);
        if (!tab) return;

        const root = tab.closest(ROOT_SELECTOR);
        if (!root) return;

        const tabs = Array.prototype.slice.call(root.querySelectorAll(TAB_SELECTOR));
        if (!tabs.length) return;

        const currentIndex = tabs.indexOf(tab);
        let nextIndex = currentIndex;

        switch (event.key) {
            case 'ArrowLeft':  nextIndex = (currentIndex - 1 + tabs.length) % tabs.length; break;
            case 'ArrowRight': nextIndex = (currentIndex + 1) % tabs.length;               break;
            case 'Home':       nextIndex = 0;                                              break;
            case 'End':        nextIndex = tabs.length - 1;                                break;
        }

        event.preventDefault();
        tabs[nextIndex].focus();
        activate(root, tabs[nextIndex]);
    });
})();

